import { useModalProvider } from 'context/ModalProviderContext';
import { getDownloadURL, ref } from 'firebase/storage';
import { useForm } from 'hooks';
import { useDocument } from 'hooks/useDocument';
import { ConfirmationModal } from 'modals/ConfirmationModal';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { app, storage } from 'services';
import { OPERATION_MODES, Request } from 'services/api/getSubscriptionRevenueData';
import { splitSubscriptionsRevenueByPlayTime } from 'services/api/splitSubscriptionsRevenueByPlayTime';
import { FlexContainer } from 'shared/Typhography/flex-box';
import { devlog, getEnumValuesRegex, getPreviousMonthUTC } from 'utilities';
import * as yup from 'yup';

import { Dot } from '../components/Dot';

import { useSubscriptionRevenueData } from './useSubscriptionRevenueData';

type ScreenStates = 'awaiting-input' | 'loading' | 'submitting' | 'error';

type AdminPanelSettings = {
  projects: Array<{ id: string; label: string }>;
};

const subscriptionRevenueRequest: Request<'get_processed_months'> = {
  endpoint: 'get_processed_months',
  endpointInput: {
    projectId: app.options.projectId as string,
    incomeType: 'playTime',
  },
};

const previousMonthDateUTC = getPreviousMonthUTC();
const formSchema = yup.object({
  date: yup
    .date()
    .max(getPreviousMonthUTC(), (context) => {
      const max =
        context.max instanceof Date
          ? `${context.max.getUTCMonth() + 1}/${context.max.getUTCFullYear()}`
          : context.max;

      return `Date must be ${max} or earlier.`;
    })
    .required(),
  project: yup
    .object({ value: yup.string().required(), label: yup.string() })
    .required('Pick a project.'),
  amount: yup
    .number()
    .typeError('Enter a number.')
    .moreThan(0, 'Amount must be greater than 0.')
    .required(),
  mode: yup
    .string<'processAndReport' | 'processAndReportForce' | 'reportOnly'>()
    .matches(getEnumValuesRegex(OPERATION_MODES))
    .required('Select a mode of execution.'),
});

type FormFields = {
  date: Date;
  project: { value: string; label: string } | null;
  amount: number;
  mode: keyof typeof OPERATION_MODES | null;
};

export const useSubscriptionSplitScreen = () => {
  const [screenState, setScreenState] = useState<ScreenStates>('loading');
  const [screenErrors, setScreenErrors] = useState<Array<string>>([]);
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  const settings = useDocument<AdminPanelSettings>('settings/adminPanel', false);
  const processedSubscriptionRevenues = useSubscriptionRevenueData(subscriptionRevenueRequest);
  const { openModal, closeModal } = useModalProvider();

  const form = useForm<FormFields>({
    schema: formSchema,
    defaultValues: {
      project: null,
      amount: '' as any, // Empty string avoids default 0 in number input
      date: previousMonthDateUTC,
      mode: null,
    },
  });

  const projectOptions = useMemo(
    () =>
      settings.data
        ? settings.data.projects.map((project: AdminPanelSettings['projects'][number]) => ({
            label: project.label,
            value: project.id,
          }))
        : [],
    [settings.data],
  );

  const initiateRevenueSplit = async (payload: yup.Asserts<typeof formSchema>) => {
    try {
      setScreenState('submitting');

      const response = await splitSubscriptionsRevenueByPlayTime({
        projectId: payload.project?.value as string,
        month: payload.date.toISOString(),
        totalRevenue: payload.amount,
        mode: payload.mode,
      });

      const httpsReference = ref(storage, response.data.csvReport);
      const downloadUrl = await getDownloadURL(httpsReference);

      setReportUrl(downloadUrl);

      processedSubscriptionRevenues.refetch();
      toast.success('Revenue split complete', { duration: 10_000 });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error), {
        duration: 10_000,
      });

      devlog('error', error);
    } finally {
      setScreenState('awaiting-input');
    }
  };

  const submitForm = form.handleSubmit(async (data) => {
    if (data.mode === OPERATION_MODES.executeAndReportForce) {
      openModal('subscription-revenue-split', ConfirmationModal as React.FC, {
        title: <h1>⚠️ Warning: Force Mode Data Override</h1>,
        content: (
          <>
            <strong>Caution:</strong> Using force mode will overwrite any existing revenue split
            data for the selected month.
            <br />
            <br />
            Please confirm that you want to proceed with force mode execution.
          </>
        ),
        confirmButtonVariant: 'danger',
        confirm: () => {
          initiateRevenueSplit(data as yup.Asserts<typeof formSchema>);
          closeModal('subscription-revenue-split');
        },
        cancel: () => {
          closeModal('subscription-revenue-split');
        },
        closeModal: () => {
          closeModal('subscription-revenue-split');
        },
      });
    } else {
      initiateRevenueSplit(data as yup.Asserts<typeof formSchema>);
    }
  });

  /**
   * Custom render for the month component in the date picker.
   * Adds a dot to the month if it has been processed.
   *
   * @param m Month index (0-11)
   * @param shortMonthText Short month name
   * @param fullMonthText Full month name
   * @param day Date object representing the currently visible year in the picker (misleadingly named 'day' in the react-datepicker API)
   */
  function renderMonthContent(m: number, shortMonthText: string, fullMonthText: string, day: Date) {
    // Date.UTC() is used to match UTC dates stored in the database.
    const renderedMonthDate = new Date(Date.UTC(day.getFullYear(), m, 1));

    const processedMonth = processedSubscriptionRevenues.data.find((documentData) => {
      return (
        documentData.date.getUTCFullYear() === renderedMonthDate.getUTCFullYear() &&
        documentData.date.getUTCMonth() === renderedMonthDate.getUTCMonth()
      );
    });

    let tooltipText = undefined;

    if (processedMonth) {
      tooltipText = `Month already processed\n`;
      tooltipText += `\n`;
      tooltipText += `Total: ${String(processedMonth.total.toFixed(2))}\n`;
      tooltipText += `Company: ${processedMonth.company.toFixed(2)}\n`;
      tooltipText += `Artist: ${processedMonth.artist.toFixed(2)}\n`;
      tooltipText += `Income type: ${processedMonth.incomeType}`;
    }

    return (
      <FlexContainer
        alignItems="center"
        justifyContent="center"
        style={{ height: '100%' }}
        title={tooltipText}
        position="relative"
      >
        {shortMonthText}
        {processedMonth ? <Dot /> : ''}
      </FlexContainer>
    );
  }

  useEffect(
    function updateScreenStateBasedOnData() {
      if (settings.state === 'success' && processedSubscriptionRevenues.state === 'success') {
        setScreenState('awaiting-input');
      } else if (settings.state === 'error' || processedSubscriptionRevenues.state === 'error') {
        const errors = [];

        settings.state === 'error' && errors.push(settings.error);
        processedSubscriptionRevenues.state === 'error' &&
          errors.push(processedSubscriptionRevenues.error);

        setScreenState('error');
        setScreenErrors(errors);
      }
    },
    [settings.state, processedSubscriptionRevenues.state],
  );

  useEffect(
    function setDefaultProjectOption() {
      if (settings.state === 'success') {
        if (projectOptions.length > 0) {
          form.setValue('project', projectOptions[0]);
        }
      }
    },
    [projectOptions, settings.state],
  );

  return {
    form,
    screenState,
    screenErrors,
    previousMonthDateUTC,
    projectOptions,
    reportUrl,
    renderMonthContent,
    submit: submitForm,
  };
};
